import React from 'react';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8 overflow-y-auto">
      <div className="max-w-4xl mx-auto bg-white shadow-sm rounded-lg p-6 sm:p-8">
        <div className="text-left">
          <h1 className="text-2xl sm:text-3xl font-bold mb-4">Terms of Service for KidQuotes</h1>

          <p className="text-sm text-gray-600 mb-8"><strong>Last Updated: January 24, 2025</strong></p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Introduction</h2>
          <p>Welcome to KidQuotes! These Terms of Service ("Terms") govern your use of the KidQuotes mobile application (the "App" or "Service"). By accessing or using KidQuotes, you agree to be bound by these Terms.</p>
          <p className="font-semibold">If you do not agree to these Terms, please do not use the App.</p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Acceptance of Terms</h2>
          <p>By creating an account or using the Service, you represent that:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>You are at least 18 years of age</li>
            <li>You have the legal capacity to enter into these Terms</li>
            <li>You will comply with all applicable laws and regulations</li>
            <li>All information you provide is accurate and current</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Description of Service</h2>
          <p>KidQuotes is a mobile application that allows parents and guardians to:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Record and preserve memorable quotes from their children</li>
            <li>Track children's milestones and memories</li>
            <li>Store photos of their children</li>
            <li>Share memories with family members through family groups</li>
            <li>Export and backup their data</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">User Content</h2>

          <h3 className="text-lg font-semibold mt-4 mb-2">Ownership</h3>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>You retain all ownership rights</strong> to the content you create and upload</li>
            <li>We do not claim any ownership of your content</li>
            <li>You grant us a limited license to store, display, and process your content solely to provide the Service</li>
          </ul>

          <h3 className="text-lg font-semibold mt-4 mb-2">Content Restrictions</h3>
          <p>You agree NOT to upload content that:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Violates any laws or regulations</li>
            <li>Infringes on others' intellectual property rights</li>
            <li>Contains malware, viruses, or harmful code</li>
            <li>Is abusive, threatening, or harassing</li>
            <li>Contains explicit or inappropriate material involving minors</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Family Sharing</h2>
          <p>When you join or create a family group:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>All shared content is visible to all family members</li>
            <li>You are responsible for who you invite to your family group</li>
            <li>You can leave a family group at any time</li>
            <li>The family creator can remove members at any time</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Privacy and Data Protection</h2>
          <p>Your privacy is important to us. Please review our <a href="/privacy" className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</a> to understand how we collect, use, and protect your information.</p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Acceptable Use</h2>

          <h3 className="text-lg font-semibold mt-4 mb-2">You Agree To:</h3>
          <ul className="list-disc pl-6 mb-4">
            <li>Use the Service for its intended purpose (preserving family memories)</li>
            <li>Respect the privacy and rights of other users</li>
            <li>Provide accurate information</li>
            <li>Comply with all applicable laws</li>
            <li>Keep your account secure</li>
          </ul>

          <h3 className="text-lg font-semibold mt-4 mb-2">You Agree NOT To:</h3>
          <ul className="list-disc pl-6 mb-4">
            <li>Use the Service for any illegal purpose</li>
            <li>Attempt to gain unauthorized access to the Service</li>
            <li>Interfere with or disrupt the Service</li>
            <li>Reverse engineer or attempt to extract source code</li>
            <li>Use automated systems to access the Service</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Data Backup and Export</h2>
          <p>While we maintain backups for operational purposes:</p>
          <ul className="list-disc pl-6 mb-4">
            <li><strong>You are responsible for maintaining your own backups</strong></li>
            <li>Use the Export feature regularly to download your data</li>
            <li>We are not liable for any data loss</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Disclaimers</h2>
          <p className="font-semibold mb-4">THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND</p>
          <p>KidQuotes is for entertainment and memory preservation only. It does not provide:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Medical advice</li>
            <li>Developmental guidance</li>
            <li>Professional childcare advice</li>
            <li>Educational services</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Limitation of Liability</h2>
          <p>TO THE MAXIMUM EXTENT PERMITTED BY LAW, we are not liable for:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Indirect, incidental, or consequential damages</li>
            <li>Loss of profits, data, or goodwill</li>
            <li>Service interruptions or errors</li>
            <li>Third-party actions or content</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Termination</h2>
          <p>You may terminate your account at any time through Settings &gt; Data Management. Upon termination:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Your account will be permanently deleted</li>
            <li>All your data will be removed from our systems</li>
            <li>You will lose access to all content and features</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Changes to These Terms</h2>
          <p>We may update these Terms from time to time. We will notify you by:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>Updating the "Last Updated" date</li>
            <li>Sending an email to your registered address (for material changes)</li>
            <li>Displaying an in-app notification</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3">Contact Information</h2>
          <p>For questions about these Terms of Service:</p>
          <p className="mt-4">
            <strong>Email</strong>: kidquotesapp@gmail.com<br />
            <strong>App</strong>: Settings &gt; Support &amp; About &gt; Contact Support
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3">Acknowledgment</h2>
          <p className="font-semibold">BY USING KIDQUOTES, YOU ACKNOWLEDGE THAT:</p>
          <ul className="list-disc pl-6 mb-4">
            <li>You have read and understood these Terms</li>
            <li>You agree to be bound by these Terms</li>
            <li>You have reviewed our Privacy Policy</li>
            <li>You are legally able to enter into this agreement</li>
          </ul>

          <div className="mt-12 pt-8 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              <strong>KidQuotes</strong><br />
              Version 1.0.0<br />
              © 2025
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
